import { defaultTheme } from 'react-admin';
import { alpha, createTheme } from '@mui/material/styles';

const fontFamily = [
    '"Fira Sans"',
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    'Arial',
    'sans-serif',
].join(',');

const createAdminTheme = (mode) => {
    const dark = mode === 'dark';
    const primary = dark ? '#68D391' : '#176B4D';
    const divider = dark ? '#2B4038' : '#DDE7E2';
    const paper = dark ? '#14211D' : '#FFFFFF';
    const canvas = dark ? '#0C1613' : '#F4F7F5';
    const text = dark ? '#F1F7F4' : '#17251F';
    const muted = dark ? '#9CB0A7' : '#66776F';

    return createTheme({
        ...defaultTheme,
        palette: {
            mode,
            primary: {
                main: primary,
                light: dark ? '#8BE0AB' : '#3B8B6D',
                dark: dark ? '#3BA467' : '#0E4F38',
                contrastText: dark ? '#0B2117' : '#FFFFFF',
            },
            secondary: {
                main: dark ? '#F1BF66' : '#B8731A',
                light: '#F7D89E',
                dark: '#8A5613',
                contrastText: dark ? '#2B1B06' : '#FFFFFF',
            },
            success: { main: dark ? '#62D394' : '#17834F' },
            warning: { main: dark ? '#F2C166' : '#B66C0A' },
            error: { main: dark ? '#FF8A8A' : '#C53D42' },
            info: { main: dark ? '#75C7E8' : '#247BA0' },
            background: { default: canvas, paper },
            text: { primary: text, secondary: muted },
            divider,
            action: {
                hover: alpha(primary, dark ? 0.12 : 0.07),
                selected: alpha(primary, dark ? 0.18 : 0.11),
                focus: alpha(primary, 0.18),
            },
        },
        typography: {
            fontFamily,
            h1: { fontSize: '2rem', lineHeight: 1.2, fontWeight: 750, letterSpacing: '-0.035em' },
            h2: { fontSize: '1.75rem', lineHeight: 1.25, fontWeight: 750, letterSpacing: '-0.03em' },
            h3: { fontSize: '1.5rem', lineHeight: 1.3, fontWeight: 700, letterSpacing: '-0.025em' },
            h4: { fontSize: '1.3rem', lineHeight: 1.35, fontWeight: 700, letterSpacing: '-0.02em' },
            h5: { fontSize: '1.12rem', lineHeight: 1.4, fontWeight: 700, letterSpacing: '-0.015em' },
            h6: { fontSize: '1rem', lineHeight: 1.45, fontWeight: 700 },
            subtitle1: { fontWeight: 650 },
            body1: { fontSize: '0.94rem', lineHeight: 1.55 },
            body2: { fontSize: '0.86rem', lineHeight: 1.5 },
            button: { textTransform: 'none', fontWeight: 650, letterSpacing: 0 },
            caption: { fontSize: '0.76rem', lineHeight: 1.45 },
        },
        shape: { borderRadius: 12 },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    body: { scrollbarColor: `${dark ? '#3D574D' : '#B7C8C0'} transparent` },
                    '::selection': { backgroundColor: alpha(primary, 0.22) },
                    '*:focus-visible': { outline: `3px solid ${alpha(primary, 0.32)}`, outlineOffset: 2 },
                },
            },
            MuiButtonBase: {
                defaultProps: { disableRipple: true },
                styleOverrides: { root: { transition: 'background-color 160ms ease, color 160ms ease, border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease' } },
            },
            MuiButton: {
                defaultProps: { disableElevation: true },
                styleOverrides: {
                    root: { minHeight: 40, borderRadius: 10, padding: '7px 14px' },
                    sizeSmall: { minHeight: 34, padding: '5px 11px' },
                    contained: { boxShadow: `0 6px 16px ${alpha(primary, 0.16)}`, '&:hover': { boxShadow: `0 8px 20px ${alpha(primary, 0.24)}`, transform: 'translateY(-1px)' } },
                    outlined: { borderColor: divider, '&:hover': { borderColor: alpha(primary, 0.5), backgroundColor: alpha(primary, 0.06) } },
                },
            },
            MuiIconButton: {
                styleOverrides: { root: { borderRadius: 10, '&:hover': { backgroundColor: alpha(primary, 0.09) } } },
            },
            MuiPaper: {
                styleOverrides: {
                    root: { backgroundImage: 'none' },
                    rounded: { borderRadius: 14 },
                    elevation1: { boxShadow: dark ? '0 12px 30px rgba(0,0,0,.20)' : '0 10px 28px rgba(20,55,40,.07)' },
                    elevation8: { boxShadow: dark ? '0 20px 54px rgba(0,0,0,.38)' : '0 22px 54px rgba(20,55,40,.16)' },
                },
            },
            MuiCard: {
                styleOverrides: { root: { border: `1px solid ${divider}`, boxShadow: dark ? '0 10px 28px rgba(0,0,0,.16)' : '0 8px 24px rgba(20,55,40,.055)' } },
            },
            MuiTextField: { defaultProps: { variant: 'outlined' } },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        minHeight: 44,
                        borderRadius: 10,
                        backgroundColor: dark ? alpha('#FFFFFF', 0.025) : '#FFFFFF',
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha(primary, 0.5) },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
                    },
                    notchedOutline: { borderColor: divider },
                },
            },
            MuiInputLabel: { styleOverrides: { root: { fontSize: '0.9rem' } } },
            MuiFormHelperText: { styleOverrides: { root: { marginLeft: 2, marginTop: 5 } } },
            MuiDialog: {
                styleOverrides: { paper: { border: `1px solid ${divider}`, borderRadius: 18 } },
            },
            MuiDialogTitle: { styleOverrides: { root: { padding: '22px 24px 12px', fontSize: '1.2rem', fontWeight: 720 } } },
            MuiDialogContent: { styleOverrides: { root: { padding: '12px 24px 22px' } } },
            MuiDialogActions: { styleOverrides: { root: { padding: '14px 24px 22px' } } },
            MuiTableCell: {
                styleOverrides: {
                    root: { padding: '13px 16px', borderBottom: `1px solid ${divider}` },
                    head: { paddingTop: 11, paddingBottom: 11, backgroundColor: dark ? '#192A24' : '#F7FAF8', color: muted, fontSize: '0.72rem', fontWeight: 750, textTransform: 'uppercase', letterSpacing: '0.055em', whiteSpace: 'nowrap' },
                },
            },
            MuiTableRow: { styleOverrides: { root: { '&:last-child td': { borderBottom: 0 }, '&:hover': { backgroundColor: `${alpha(primary, 0.045)} !important` } } } },
            MuiChip: { styleOverrides: { root: { height: 28, borderRadius: 8, fontWeight: 650 }, sizeSmall: { height: 24 } } },
            MuiTooltip: { styleOverrides: { tooltip: { borderRadius: 8, padding: '7px 10px', fontSize: '0.75rem' } } },
            MuiToggleButton: { styleOverrides: { root: { minHeight: 36, borderColor: divider, textTransform: 'none', fontWeight: 650 } } },
            MuiMenuItem: { styleOverrides: { root: { minHeight: 40, margin: '3px 6px', borderRadius: 8 } } },
            RaDatagrid: {
                styleOverrides: {
                    root: {
                        '& .RaDatagrid-tableWrapper': { borderRadius: 14 },
                        '& .RaDatagrid-rowEven': { backgroundColor: 'transparent' },
                        '& .RaDatagrid-clickableRow': { cursor: 'pointer' },
                    },
                },
            },
            RaList: { styleOverrides: { root: { '& .RaList-main': { gap: 12 } } } },
            RaSimpleForm: { styleOverrides: { root: { '& .MuiFormControl-root': { maxWidth: '100%' } } } },
            RaNotification: { defaultProps: { anchorOrigin: { vertical: 'bottom', horizontal: 'right' } } },
        },
    });
};

export const lightTheme = createAdminTheme('light');
export const darkTheme = createAdminTheme('dark');
